/**
 * Question Mapper Utility
 * Maps LazyQuestion from useLazyQuestions hook to DemoQuestion format
 */

import type { DemoQuestion, ClusterContext } from '@/components/exam/types';

interface LazyQuestion {
  id: string;
  questionId?: string;
  order: number;
  clusterId?: string;
  questionClusterId?: string;
  type?: string;
  context?: string;
  stem?: string;
  title?: string;
  category?: string;
  points?: number;
  subject?: string;
  options?: any[];
  trueFalseItems?: any[];
  matchingPrompts?: any[];
  matchingOptions?: any[];
  fillBlankAnswers?: any[];
  correct?: number | boolean;
  correctKeywords?: string[];
  matchingCorrect?: Record<string, string>;
  minWords?: number;
  maxWords?: number;
  content?: {
    minWords?: number;
    maxWords?: number;
    context?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

const normalizeQuestionType = (type: string | undefined): DemoQuestion['type'] => {
  if (!type) return 'mc';
  const t = type.toLowerCase();

  // Map backend types to frontend DemoQuestion types
  if (t === 'mc' || t === 'mcq' || t.includes('multiple')) return 'mc';
  if (
    t === 'tf' ||
    t === 'truefalse' ||
    t === 'true_false' ||
    t.includes('true') ||
    t === 'tf_matrix'
  )
    return 'trueFalse'; // standardizing on trueFalse
  if (t === 'matching' || t.includes('match')) return 'matching';
  if (t === 'fill_blank' || t === 'fill-blank' || t === 'fillblank' || t.includes('fill'))
    return 'fillBlank';
  if (t === 'short' || t === 'short_answer' || t === 'short-answer' || t.includes('short'))
    return 'short';
  if (t === 'essay') return 'essay';
  if (t === 'cluster') return 'cluster';

  // Fallback usage but try to respect input if it matches known types
  return t as DemoQuestion['type'];
};

/**
 * Maps a LazyQuestion to DemoQuestion format
 * @param lazyQ - Question from useLazyQuestions hook
 * @param clusterContext - Optional cluster context from clusters Map
 * @returns DemoQuestion ready for rendering
 */
export function mapLazyQuestionToDemoQuestion(
  lazyQ: LazyQuestion,
  clusterContext?: ClusterContext
): DemoQuestion {
  return {
    id: lazyQ.id || lazyQ.questionId || '',
    type: normalizeQuestionType(lazyQ.type),
    subject: (lazyQ.subject || 'english').toLowerCase(),
    stem: lazyQ.stem || '',
    category: lazyQ.category,
    points: lazyQ.points ?? 6,

    // Answer options
    options: lazyQ.options || [],
    trueFalseItems: lazyQ.trueFalseItems || [],
    matchingPrompts: lazyQ.matchingPrompts || [],
    matchingOptions: lazyQ.matchingOptions || [],
    fillBlankAnswers: lazyQ.fillBlankAnswers || [],

    // Correct answers (if available - shouldn't be in production)
    correct: lazyQ.correct,
    correctKeywords: lazyQ.correctKeywords,
    matchingCorrect: lazyQ.matchingCorrect,
    minWords: lazyQ.minWords ?? lazyQ.content?.minWords,
    maxWords: lazyQ.maxWords ?? lazyQ.content?.maxWords,

    // Cluster info
    clusterId: lazyQ.clusterId,
    clusterContext: clusterContext,
    questionClusterId: lazyQ.questionClusterId,

    // Cluster metadata (for navigation)
    clusterOrder: undefined, // Not available in lazy mode
    clusterQuestionIndex: undefined,
    clusterQuestionCount: undefined,
    isClusterIntro: false,
    isLoaded: lazyQ.isLoaded,
  };
}

/**
 * Converts LazyQuestions Map to DemoQuestion array
 * @param lazyQuestionsMap - Map from useLazyQuestions
 * @param clustersMap - Clusters Map from useLazyQuestions
 * @returns Sorted array of DemoQuestions
 */
export function convertLazyQuestionsMapToArray(
  lazyQuestionsMap: Map<number, LazyQuestion>,
  clustersMap: Map<string, ClusterContext>
): DemoQuestion[] {
  return Array.from(lazyQuestionsMap.values())
    .sort((a, b) => a.order - b.order)
    .map(lazyQ => {
      const clusterContext = lazyQ.clusterId ? clustersMap.get(lazyQ.clusterId) : undefined;

      return mapLazyQuestionToDemoQuestion(lazyQ, clusterContext);
    });
}
